import { useTouchDevice } from '@/hooks/use-touch-device';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import {
    BottomOverlay,
    ProfileHeader,
    StoryImage,
    STORY_DURATION_MS,
    useBodyScrollLock,
    useLikes,
    useOpenPhase,
    type StoryViewerProfile,
} from './story-viewer';

// ---------------------------------------------------------------------------
// Internal slide types
// ---------------------------------------------------------------------------

interface StorySlide {
    type: 'story';
    story: import('@/types').GalleryPhoto;
    profile: StoryViewerProfile;
    profileIndex: number;
    storyIndexInProfile: number;
}

interface AdSlide {
    type: 'ad';
    id: string;
}

type Slide = StorySlide | AdSlide;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildSlides(profiles: StoryViewerProfile[]): Slide[] {
    const result: Slide[] = [];
    profiles.forEach((profile, profileIndex) => {
        profile.stories.forEach((story, storyIndexInProfile) => {
            result.push({ type: 'story', story, profile, profileIndex, storyIndexInProfile });
        });
        if (profileIndex < profiles.length - 1) {
            result.push({ type: 'ad', id: `ad-after-${profileIndex}` });
        }
    });
    return result;
}

function AdSlideContent() {
    const { t } = useTranslation('common');
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-br from-zinc-900 to-zinc-800 px-8 text-center">
            <div className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold tracking-wider text-white/60 uppercase">
                {t('stories.ad_label')}
            </div>
            <div className="flex h-40 w-full items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                <span className="text-4xl">✦</span>
            </div>
            <p className="text-sm text-white/50">{t('stories.ad_cta')}</p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// MultiStoryViewer — cross-profile with ad injection
// ---------------------------------------------------------------------------

export interface MultiStoryViewerProps {
    profiles: StoryViewerProfile[];
    open: boolean;
    onClose: () => void;
    /** Which profile to start on (index into `profiles`). Default 0. */
    initialProfileIndex?: number;
}

export function MultiStoryViewer({
    profiles,
    open,
    onClose,
    initialProfileIndex = 0,
}: MultiStoryViewerProps) {
    const { t } = useTranslation('common');
    const isTouch = useTouchDevice();
    const { isLiked, toggle: toggleLike } = useLikes();
    const phase = useOpenPhase(open);
    useBodyScrollLock(open);

    const slides = useMemo(() => buildSlides(profiles), [profiles]);

    const firstSlideIndexForProfile = useCallback(
        (profileIdx: number) =>
            slides.findIndex((s) => s.type === 'story' && (s as StorySlide).profileIndex === profileIdx),
        [slides],
    );

    const storySlideIndicesForProfile = useCallback(
        (profileIdx: number) =>
            slides
                .map((s, i) => ({ s, i }))
                .filter(({ s }) => s.type === 'story' && (s as StorySlide).profileIndex === profileIdx)
                .map(({ i }) => i),
        [slides],
    );

    const initialSlide = useMemo(
        () => Math.max(0, firstSlideIndexForProfile(initialProfileIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const [emblaRef, emblaApi] = useEmblaCarousel({
        axis: 'x',
        startIndex: initialSlide,
        watchDrag: isTouch,
        loop: false,
        align: 'center',
    });

    const [current, setCurrent] = useState(initialSlide);
    const [progress, setProgress] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const startTimeRef = useRef<number>(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setCurrent(emblaApi.selectedScrollSnap());
        setProgress(0);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.on('select', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    // Jump to correct profile when viewer opens or initialProfileIndex changes
    useEffect(() => {
        if (!emblaApi || !open) return;
        const idx = firstSlideIndexForProfile(initialProfileIndex);
        if (idx >= 0) {
            emblaApi.scrollTo(idx, true);
            setCurrent(idx);
            setProgress(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialProfileIndex]);

    const goNext = useCallback(() => {
        if (!emblaApi) return;
        if (current >= slides.length - 1) {
            onClose();
            return;
        }
        emblaApi.scrollNext();
    }, [emblaApi, current, slides.length, onClose]);

    const goPrev = useCallback(() => {
        if (!emblaApi) return;
        if (current <= 0) {
            onClose();
            return;
        }
        emblaApi.scrollPrev();
    }, [emblaApi, current, onClose]);

    const currentSlide = slides[current] as Slide | undefined;
    const isAdSlide = currentSlide?.type === 'ad';

    const startTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current;
            setProgress(Math.min((elapsed / STORY_DURATION_MS) * 100, 100));
            if (elapsed >= STORY_DURATION_MS) goNext();
        }, 50);
    }, [goNext]);

    useEffect(() => {
        if (!open || phase === 'closed' || isAdSlide) {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }
        startTimer();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [open, current, phase, startTimer, isAdSlide]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!open) return;
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, goNext, goPrev, onClose]);

    const touchStartY = useRef<number>(0);
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartY.current = e.touches[0].clientY;
    }, []);
    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            if (e.changedTouches[0].clientY - touchStartY.current > 80) onClose();
        },
        [onClose],
    );

    if (phase === 'closed') return null;

    const isVisible = phase === 'open';
    const storySlide = currentSlide?.type === 'story' ? (currentSlide as StorySlide) : null;
    const currentProfile = storySlide?.profile ?? null;
    const currentStory = storySlide?.story ?? null;
    const currentProfileIdx = storySlide?.profileIndex ?? -1;
    const profileSlideIndices = currentProfileIdx >= 0 ? storySlideIndicesForProfile(currentProfileIdx) : [];

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300"
            style={{
                backgroundColor: isVisible ? 'rgba(0,0,0,0.92)' : 'rgba(0,0,0,0)',
                backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
            }}
            onClick={onClose}
        >
            <div
                className="relative h-full max-h-[100dvh] w-full max-w-md overflow-hidden bg-black transition-all duration-300 md:max-h-[88vh] md:max-w-[390px] md:rounded-2xl"
                style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(16px)',
                }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {/* Progress bars (current profile's stories only) */}
                <div className="absolute top-0 right-0 left-0 z-10 flex gap-1 p-2">
                    {isAdSlide ? (
                        <div className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
                            <div className="h-full w-full bg-white" />
                        </div>
                    ) : (
                        profileSlideIndices.map((slideIdx) => (
                            <div key={slideIdx} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
                                <div
                                    className="h-full bg-white transition-none"
                                    style={{
                                        width:
                                            slideIdx < current
                                                ? '100%'
                                                : slideIdx === current
                                                  ? `${progress}%`
                                                  : '0%',
                                    }}
                                />
                            </div>
                        ))
                    )}
                </div>

                {/* Top bar */}
                <div className="absolute top-4 right-0 left-0 z-10 flex items-center justify-between gap-2 px-3 pt-3">
                    {currentProfile ? (
                        <ProfileHeader profile={currentProfile} onClose={onClose} />
                    ) : (
                        <>
                            <div />
                            <button
                                className="shrink-0 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-sm"
                                onClick={onClose}
                                aria-label={t('actions.close')}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>

                {/* Embla viewport */}
                <div ref={emblaRef} className="h-full w-full overflow-hidden">
                    <div className="flex h-full">
                        {slides.map((slide, i) => {
                            if (slide.type === 'ad') {
                                return (
                                    <div key={slide.id} className="relative h-full w-full shrink-0">
                                        <AdSlideContent />
                                    </div>
                                );
                            }
                            const s = (slide as StorySlide).story;
                            return <StoryImage key={s.id ?? i} story={s} index={i} />;
                        })}
                    </div>
                </div>

                {!isAdSlide && currentProfile && currentStory && (
                    <BottomOverlay
                        story={currentStory}
                        profile={currentProfile}
                        isLiked={isLiked}
                        toggleLike={toggleLike}
                    />
                )}

                {/* Desktop tap zones */}
                {!isTouch && (
                    <>
                        <button
                            className="absolute inset-y-0 left-0 w-1/3 cursor-pointer"
                            onClick={goPrev}
                            aria-label={t('a11y.prev_story')}
                        />
                        <button
                            className="absolute inset-y-0 right-0 w-1/3 cursor-pointer"
                            onClick={goNext}
                            aria-label={t('a11y.next_story')}
                        />
                    </>
                )}

                {/* Desktop arrow hints */}
                {!isTouch && current > 0 && (
                    <div className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
                        <ChevronLeft className="h-8 w-8 text-white/50" />
                    </div>
                )}
                {!isTouch && current < slides.length - 1 && (
                    <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                        <ChevronRight className="h-8 w-8 text-white/50" />
                    </div>
                )}
            </div>
        </div>
    );
}
